import React from 'react'
import { Helmet } from 'react-helmet-async'

const SITE_URL = 'https://dhgc.org.in'

const normalizeCanonicalUrl = (value) => {
  const fallbackPath = typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}${window.location.hash}` : '/'
  const source = value || fallbackPath

  try {
    const parsed = source.startsWith('http') ? new URL(source) : new URL(source, SITE_URL)
    const normalized = new URL(`${parsed.pathname}${parsed.search}${parsed.hash}`, SITE_URL)
    return normalized.href
  } catch {
    return SITE_URL
  }
}

const SEOHead = ({ 
  title = 'Dr. Haribhai Godhani Campus | Schools and Colleges in Junagadh, Gujarat',
  description = 'Official website of Dr. Haribhai Godhani Campus in Junagadh, Gujarat. Explore admissions, academic programs, annual planners, campus updates, staff details, alumni stories, and trust information.',
  keywords = 'Dr. Haribhai Godhani Campus, DHGC Junagadh, schools in Junagadh, colleges in Junagadh, admissions Junagadh, academic programs, annual planner, campus infrastructure, staff directory, alumni stories',
  canonical,
  image = '',
  type = 'website',
  noIndex = false,
  structuredData = [],
  alternateLinks = [],
  children 
}) => {
  const url = normalizeCanonicalUrl(canonical)
  const siteName = 'Dr. Haribhai Godhani Campus'
  const fullImageUrl = image ? (image.startsWith('http') ? image : `${SITE_URL}${image.startsWith('/') ? '' : '/'}${image}`) : `${SITE_URL}/logo.jpg`
  const robotsDefault = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
  const robotsContent = noIndex ? 'noindex, nofollow' : robotsDefault
  const crawlerContent = noIndex ? 'noindex, nofollow' : 'index, follow'
  
  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="Dr. Haribhai Godhani Campus - Sardar Patel Educational Trust" />
      <meta name="robots" content={robotsContent} />
      <meta name="googlebot" content={crawlerContent} />
      <meta name="bingbot" content={crawlerContent} />
      <meta name="language" content="English, Gujarati" />
      <meta http-equiv="content-language" content="en, gu" />
      <meta name="revisit-after" content="7 days" />
      <meta name="rating" content="General" />
      <meta name="distribution" content="Global" />
      
      {/* Geo Tags */}
      <meta name="geo.region" content="IN-GJ" />
      <meta name="geo.placename" content="Junagadh, Gujarat" />
      <meta name="geo.position" content="21.3493;70.7512" />
      <meta name="ICBM" content="21.3493, 70.7512" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={url} />
      {alternateLinks.map((link) => (
        link?.href && link?.hrefLang ? (
          <link rel="alternate" href={link.href} hrefLang={link.hrefLang} key={`${link.href}-${link.hrefLang}`} />
        ) : null
      ))}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:image:secure_url" content={fullImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="Dr. Haribhai Godhani Campus Logo" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_IN" />
      <meta property="og:locale:alternate" content="gu_IN" />
      <meta property="og:locale:alternate" content="hi_IN" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImageUrl} />
      <meta name="twitter:image:alt" content="Dr. Haribhai Godhani Campus Logo" />
      <meta name="twitter:site" content="@spetjunagadh" />
      <meta name="twitter:creator" content="@spetjunagadh" />
      
      {/* Favicon */}
      <link rel="icon" type="image/x-icon" href="/favicons/favicon.ico" />
      <link rel="apple-touch-icon" sizes="180x180" href="/favicons/favicon-180x180.png" />
      
      {/* Additional meta tags */}
      <meta name="theme-color" content="#8C0046" />
      <meta name="msapplication-TileColor" content="#8C0046" />
      <meta name="msapplication-config" content="/browserconfig.xml" />
      
      {/* Viewport for mobile optimization */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />
      
      {structuredData.map((schema, index) => (
        <script type="application/ld+json" key={index}>
          {JSON.stringify(schema)}
        </script>
      ))}

      {children}
    </Helmet>
  )
}

export default SEOHead
